<template>
  <div class="potree-container">
    <!-- Mouse Position Display -->
    <div class="mouse-position-sheet" v-if="mousePosition.visible">
      <v-icon dark>mdi-cursor-default</v-icon>
      <div class="position-coords">
        <span class="coord-item">
          <span class="coord-label">X:</span>
          <span class="coord-value">{{ mousePosition.x }}</span>
        </span>
        <span class="coord-item">
          <span class="coord-label">Y:</span>
          <span class="coord-value">{{ mousePosition.y }}</span>
        </span>
        <span class="coord-item">
          <span class="coord-label">Z:</span>
          <span class="coord-value">{{ mousePosition.z }}</span>
        </span>
        <!-- Corrosion Distance Display -->
        <span
          v-if="corrosionMode.enabled && mousePosition.corrosionDistance !== null"
          class="coord-item corrosion-coord"
          :class="getCorrosionSeverityClass(mousePosition.corrosionDistance)"
        >
          <span class="coord-label">Corr:</span>
          <span class="coord-value corrosion-value">{{ formatCorrosionDistance(mousePosition.corrosionDistance) }}</span>
        </span>
      </div>
    </div>
    
    <div id="potree_render_area" ref="renderArea"></div>
    <div id="potree_sidebar_container" ref="sidebarContainer"></div>
  </div>
</template>

<script>
import {
  logComponentMounted,
  logComponentDestroyed,
  logViewerInit,
  logViewerReady,
  logPointCloudLoading,
  logPointCloudLoaded,
  logPointCloudError,
  startTimer,
  log as debugLog,
  warn as debugWarn,
  error as debugError
} from '@/utils/debugLogger'

import {
  calculatePointDistancesToCylinder,
  getCorrosionStatistics,
  exportCorrosionDataToCSV
} from '../utils/corrosionDetection'

import {
  CORROSION_GRADIENT,
  getGradientByStyle,
  createCustomGradient
} from '../utils/corrosionGradient'

import {
  applyCorrosionShader,
  disableCorrosionShader,
  updateCorrosionGradient,
  updateCorrosionThresholds
} from '../utils/corrosionShader'

import HttpService from '@/utils/httpService'

export default {
  name: 'PotreeViewer',

  props: {
    pointCloudUrl: {
      type: String,
      default: './pointclouds/test/metadata.json'
    },
    pointCloudName: {
      type: String,
      default: 'test'
    },
    pointBudget: {
      type: Number,
      default: Infinity
    },
    fov: {
      type: Number,
      default: 60
    },
    background: {
      type: String,
      default: 'gradient'
    },
    language: {
      type: String,
      default: 'en'
    },
    showNativeTools: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      viewer: null,
      pointcloud: null,
      loading: true,
      viewerReady: false,
      pointCloudReady: false,
      mousePosition: {
        visible: false,
        x: '---',
        y: '---',
        z: '---',
        corrosionDistance: null
      },
      // Corrosion visualization state
      corrosionMode: {
        enabled: false,
        distances: null,
        allPoints: null,
        statistics: null,
        cylinderData: null,
        originalMaterialState: null,
        gradientStyle: 'default',
        clipVolumeActive: false
      }
    }
  },

  mounted() {
    logComponentMounted('PotreeViewer', {
      pointCloudUrl: this.pointCloudUrl,
      pointCloudName: this.pointCloudName,
      pointBudget: this.pointBudget,
      fov: this.fov,
      background: this.background
    })
    this.initializePotree()
  },

  beforeDestroy() {
    logComponentDestroyed('PotreeViewer', {
      viewerReady: this.viewerReady,
      pointCloudReady: this.pointCloudReady
    })
    this.cleanup()
  },

  methods: {
    initializePotree() {
      const viewerTimer = startTimer('Potree Viewer Initialization')
      
      logViewerInit('Potree Viewer', {
        library: typeof window.Potree !== 'undefined' ? 'loaded' : 'missing',
        renderArea: this.$refs.renderArea ? 'ready' : 'missing'
      })

      if (typeof window.Potree === 'undefined') {
        debugError('Potree library not loaded')
        logPointCloudError('Potree Library', { error: 'Library not loaded' })
        this.$emit('potree-error', new Error('Potree library not loaded'))
        return
      }

      try {
        // Enable preserveDrawingBuffer for screenshots (MUST be before viewer creation)
        if (!window.potreeCanvasPatched) {
          const origGetContext = HTMLCanvasElement.prototype.getContext;
          HTMLCanvasElement.prototype.getContext = function(type, attribs) {
            attribs = attribs || {};
            attribs.preserveDrawingBuffer = true;
            return origGetContext.call(this, type, attribs);
          };
          window.potreeCanvasPatched = true;
          debugLog('[PotreeViewer] Canvas patched for screenshot capability');
        }

        // Initialize Potree viewer
        this.viewer = new window.Potree.Viewer(this.$refs.renderArea)

        this.viewer.useHQ = true;
        this.viewer.setEDLEnabled(true)
        // this.viewer.setEDLStrength(0.1)
        // this.viewer.setEDLRadius(0.1)
        this.viewer.compass.setVisible(true)


        // Configure viewer settings
        if (this.viewer.setFOV) this.viewer.setFOV(this.fov)
        if (this.viewer.setPointBudget) this.viewer.setPointBudget(this.pointBudget)
        if (this.viewer.loadSettingsFromURL) this.viewer.loadSettingsFromURL()
        if (this.viewer.setBackground) this.viewer.setBackground(this.background)

        // Set description
        if (this.viewer.setDescription) {
          this.viewer.setDescription("AIVA Potree Measurements")
        }

        this.viewerReady = true
        
        logViewerReady('Potree Viewer', {
          useHQ: this.viewer.useHQ,
          EDLEnabled: true,
          fov: this.fov,
          pointBudget: this.pointBudget,
          background: this.background
        })
        
        this.$emit('viewer-ready', this.viewer)
        
        viewerTimer.end('Viewer Initialized')

        // Load GUI and conditionally show native tools
        if (this.viewer.loadGUI) {
          this.viewer.loadGUI(() => {
            if (this.viewer.setLanguage) this.viewer.setLanguage(this.language)

            if (this.showNativeTools) {
              // Enable native Potree sidebar with all tools
              if (typeof window.$ !== 'undefined') {
                window.$("#menu_tools").next().show()
                window.$("#menu_clipping").next().show()
                window.$("#menu_scene").next().show()
                window.$("#menu_filters").next().show()
                window.$("#menu_appearance").next().show()
                window.$("#menu_about").next().show()
              }
              // Show the sidebar by default
              if (this.viewer.toggleSidebar) this.viewer.toggleSidebar()
            } else {
              // Hide native sidebar if tools are disabled
              if (this.$refs.sidebarContainer) {
                this.$refs.sidebarContainer.style.display = 'none'
              }
            }
          })
        }

        // Load point cloud
        this.loadPointCloud()

      } catch (error) {
        debugError('Error initializing Potree viewer:', error)
        logPointCloudError('Potree Initialization', { 
          error: error.message,
          stack: error.stack 
        })
        this.$emit('potree-error', error)
      }
    },

    async loadPointCloud() {
      if (!this.viewer) return

      const pointCloudTimer = startTimer('Point Cloud Loading')

      logPointCloudLoading('Point Cloud', {
        url: this.pointCloudUrl,
        name: this.pointCloudName
      })

      this.loading = true

      try {
        // Fetch signed URLs and initialize PotreeSignedUrls (handles Azure SAS and S3)
        // Pass point cloud name to support multiple point clouds with separate signed URL managers
        const metadataUrl = await window.PotreeSignedUrls.fetchAndInit(this.pointCloudUrl, HttpService, this.pointCloudName)

        window.Potree.loadPointCloud(metadataUrl, this.pointCloudName, (e) => {
          try {
            const scene = this.viewer.scene
            this.pointcloud = e.pointcloud

            if (this.pointcloud && this.pointcloud.material) {
              const material = this.pointcloud.material
              material.size = 1
              if (window.Potree.PointSizeType) {
                material.pointSizeType = window.Potree.PointSizeType.ADAPTIVE
              }
              if (window.Potree.PointShape) {
                material.shape = window.Potree.PointShape.SQUARE
              }
            }

            if (scene && scene.addPointCloud) {
              scene.addPointCloud(this.pointcloud)
            }

            if (this.viewer.fitToScreen) {
              this.viewer.fitToScreen()
            }

            this.loading = false
            this.pointCloudReady = true

            logPointCloudLoaded('Point Cloud', {
              name: this.pointCloudName,
              points: this.pointcloud.numPoints || 'unknown',
              material: {
                size: this.pointcloud.material?.size,
                pointSizeType: this.pointcloud.material?.pointSizeType,
                shape: this.pointcloud.material?.shape
              }
            })

            pointCloudTimer.end('Point Cloud Loaded')

            if (!window.AVA_POTREE_VIEWERS || window.AVA_POTREE_VIEWERS.length === 0) {
              window.AVA_POTREE_VIEWERS = [this.viewer]
            } else {
              window.AVA_POTREE_VIEWERS.push(this.viewer)
            }

            
            window.AVA_POTREE_VIEWER = window.AVA_POTREE_VIEWERS[0];

            // Log standalone Potree coordinate system for debugging
            debugLog('[🔍 STANDALONE POTREE SYSTEM]', {
              standalonePotreeOffset: this.pointcloud.pcoGeometry?.offset ?
                { x: this.pointcloud.pcoGeometry.offset.x.toFixed(3), y: this.pointcloud.pcoGeometry.offset.y.toFixed(3), z: this.pointcloud.pcoGeometry.offset.z.toFixed(3) } : 'undefined',
              standalonePotreePosition: this.pointcloud.position ?
                { x: this.pointcloud.position.x.toFixed(3), y: this.pointcloud.position.y.toFixed(3), z: this.pointcloud.position.z.toFixed(3) } : 'undefined',
              standalonePotreeScale: this.pointcloud.scale?.x.toFixed(3),
              boundingBox: this.pointcloud.pcoGeometry?.boundingBox ? {
                min: { x: this.pointcloud.pcoGeometry.boundingBox.min.x.toFixed(3), y: this.pointcloud.pcoGeometry.boundingBox.min.y.toFixed(3), z: this.pointcloud.pcoGeometry.boundingBox.min.z.toFixed(3) },
                max: { x: this.pointcloud.pcoGeometry.boundingBox.max.x.toFixed(3), y: this.pointcloud.pcoGeometry.boundingBox.max.y.toFixed(3), z: this.pointcloud.pcoGeometry.boundingBox.max.z.toFixed(3) }
              } : 'undefined'
            })

            // Emit event that point cloud is loaded
            this.$emit('pointcloud-loaded', this.pointcloud)
            this.$emit('pointcloud-ready', { viewer: this.viewer, pointcloud: this.pointcloud })

            // Setup mouse tracking for 3D position display
            this.setupMouseTracking()

          } catch (setupError) {
            debugError('Error setting up point cloud:', setupError)
            logPointCloudError('Point Cloud Setup', { 
              error: setupError.message,
              stack: setupError.stack 
            })
            this.loading = false
            this.$emit('pointcloud-error', setupError)
          }
        })
      } catch (loadError) {
        debugError('Error initiating point cloud load:', loadError)
        logPointCloudError('Point Cloud Load', { 
          error: loadError.message,
          url: this.pointCloudUrl 
        })
        this.loading = false
        this.$emit('pointcloud-error', loadError)
      }
    },

    setupMouseTracking() {
      if (!this.$refs.renderArea || !this.viewer) return

      this.$refs.renderArea.addEventListener('mousemove', this.handleMouseMove)
      this.$refs.renderArea.addEventListener('mouseleave', this.handleMouseLeave)
    },

    handleMouseMove(event) {
      if (!this.viewer || !this.pointcloud) return

      try {
        const rect = this.$refs.renderArea.getBoundingClientRect()
        const mouse = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top
        }

        // Use Potree's built-in getMousePointCloudIntersection method
        const camera = this.viewer.scene.getActiveCamera()
        const pointclouds = this.viewer.scene.pointclouds

        if (!pointclouds || pointclouds.length === 0) {
          this.mousePosition.visible = false
          return
        }

        // Get intersection using Potree's Utils method
        const intersection = window.Potree.Utils.getMousePointCloudIntersection(
          mouse,
          camera,
          this.viewer,
          pointclouds,
          { pickClipped: true }
        )

        if (intersection && intersection.location) {
          const point = intersection.location
          this.mousePosition.visible = true
          this.mousePosition.x = point.x.toFixed(3)
          this.mousePosition.y = point.y.toFixed(3)
          this.mousePosition.z = point.z.toFixed(3)

          // Calculate corrosion distance if corrosion mode is enabled
          if (this.corrosionMode.enabled && this.corrosionMode.cylinderData) {
            const distance = this.calculatePointDistanceToCylinder(point, this.corrosionMode.cylinderData)
            this.mousePosition.corrosionDistance = distance
          } else {
            this.mousePosition.corrosionDistance = null
          }
        } else {
          // No intersection found
          this.mousePosition.visible = false
        }
      } catch (error) {
        debugError('Error getting 3D position:', error)
      }
    },

    handleMouseLeave() {
      this.mousePosition.visible = false
      this.mousePosition.x = '---'
      this.mousePosition.y = '---'
      this.mousePosition.z = '---'
      this.mousePosition.corrosionDistance = null
    },

    cleanup() {
      if (!this.viewer) return

      debugLog('[PotreeViewer] Starting cleanup...')

      try {
        // Cleanup PotreeSignedUrls (stops token refresh timers)
        if (window.PotreeSignedUrls && window.PotreeSignedUrls.destroy) {
          window.PotreeSignedUrls.destroy()
        }

        // Remove mouse tracking event listeners
        if (this.$refs.renderArea) {
          this.$refs.renderArea.removeEventListener('mousemove', this.handleMouseMove)
          this.$refs.renderArea.removeEventListener('mouseleave', this.handleMouseLeave)
        }
        // 1. Remove point cloud from scene
        if (this.viewer.scene && this.pointcloud) {
          if (this.viewer.scene.removePointCloud) {
            this.viewer.scene.removePointCloud(this.pointcloud)
          }

          // Dispose point cloud if possible
          if (this.pointcloud.dispose) {
            this.pointcloud.dispose()
          }
        }

        // 2. Remove measurements
        if (this.viewer.scene && this.viewer.scene.measurements) {
          const measurements = [...this.viewer.scene.measurements]
          measurements.forEach(m => {
            if (this.viewer.scene.removeMeasurement) {
              this.viewer.scene.removeMeasurement(m)
            }
          })
        }

        // 3. Dispose renderer and force WebGL context loss
        if (this.viewer.renderer) {
          // Dispose renderer
          this.viewer.renderer.dispose()

          // Force WebGL context loss to release GPU resources
          const gl = this.viewer.renderer.getContext()
          if (gl) {
            const loseContext = gl.getExtension('WEBGL_lose_context')
            if (loseContext) {
              loseContext.loseContext()
              debugLog('[PotreeViewer] WebGL context released')
            }
          }
        }

        // 4. Cancel animation frame if exists
        if (this.viewer.rafID) {
          cancelAnimationFrame(this.viewer.rafID)
        }

        // 5. Remove event listeners
        if (this.viewer.onResize) {
          window.removeEventListener('resize', this.viewer.onResize)
        }

        // 6. Clear global references
        if (window.AVA_POTREE_VIEWERS && Array.isArray(window.AVA_POTREE_VIEWERS)) {
          const index = window.AVA_POTREE_VIEWERS.indexOf(this.viewer)
          if (index > -1) {
            window.AVA_POTREE_VIEWERS.splice(index, 1)
          }
        }
        if (window.AVA_POTREE_VIEWER === this.viewer) {
          window.AVA_POTREE_VIEWER = null
        }

        // 7. Clear references
        this.viewer = null
        this.pointcloud = null
        this.viewerReady = false
        this.pointCloudReady = false

        debugLog('[PotreeViewer] Cleanup complete')
      } catch (error) {
        debugError('[PotreeViewer] Error during cleanup:', error)
      }
    },

    // Public methods for external control
    toggleSidebar() {
      if (this.viewer && this.viewer.toggleSidebar) {
        this.viewer.toggleSidebar()
      }
    },

    fitToScreen() {
      if (this.viewer && this.viewer.fitToScreen) {
        this.viewer.fitToScreen()
      }
    },

    setBackground(background) {
      if (this.viewer && this.viewer.setBackground) {
        this.viewer.setBackground(background)
      }
    },

    // Additional public API methods
    getViewer() {
      return this.viewer
    },

    getPointCloud() {
      return this.pointcloud
    },

    isViewerReady() {
      return this.viewerReady && this.viewer !== null
    },

    isPointCloudReady() {
      return this.pointCloudReady && this.pointcloud !== null
    },

    isLoading() {
      return this.loading
    },

    // Corrosion Visualization Methods

    /**
     * Get active clip volumes from the scene
     * @returns {Array} Array of active clip volume objects
     */
    getActiveClipVolumes() {
      if (!this.viewer || !this.viewer.scene || !this.viewer.scene.volumes) {
        return []
      }

      // Filter volumes where clip is enabled
      const activeClipVolumes = this.viewer.scene.volumes.filter(volume => volume.clip === true)

      return activeClipVolumes
    },

    /**
     * Apply corrosion visualization to the point cloud
     * This is the main entry point for enabling corrosion view
     *
     * @param {Object} cylinderData - Cylinder parameters {position, axis, radius, height}
     * @param {string} gradientStyle - Gradient style (default, high-contrast, thermal, monochrome)
     * @returns {Promise<Object>} Statistics object
     */
    async applyCorrosionVisualization(cylinderData, gradientStyle = 'default') {
      if (!this.viewer || !this.pointcloud) {
        throw new Error('Viewer or point cloud not ready')
      }

      if (!cylinderData) {
        throw new Error('Cylinder data is required')
      }

      debugLog('[Corrosion] Starting corrosion visualization...', cylinderData)

      try {
        // Store original material state for restoration
        if (!this.corrosionMode.originalMaterialState) {
          this.corrosionMode.originalMaterialState = {
            activeAttributeName: this.pointcloud.material.activeAttributeName,
            elevationRange: [...this.pointcloud.material.elevationRange],
            gradient: this.pointcloud.material.gradient
          }
        }

        // Query active clip volumes from the scene
        const activeClipVolumes = this.getActiveClipVolumes()

        if (activeClipVolumes.length > 0) {
          debugLog('[Corrosion Clip] Active clip volumes detected:', {
            count: activeClipVolumes.length,
            volumes: activeClipVolumes.map((v, i) => ({
              index: i,
              position: v.position ? { x: v.position.x, y: v.position.y, z: v.position.z } : null,
              scale: v.scale ? { x: v.scale.x, y: v.scale.y, z: v.scale.z } : null,
              clip: v.clip
            }))
          })
        } else {
          debugLog('[Corrosion Clip] No active clip volumes - analyzing entire point cloud')
        }

        // Step 1: Calculate distances with clip volume filtering
        this.$emit('corrosion-progress', 0)
        debugLog('[Corrosion] Step 1: Calculating point distances...')

        const visiblePointClouds = window.AVA_POTREE_VIEWER?.scene?.pointclouds?.filter((pc) => pc?.visible) || []
        const pointCloudInput = visiblePointClouds.length > 0 ? visiblePointClouds : window.AVA_POTREE_VIEWER.scene.pointclouds[0]

        const distancesData = await calculatePointDistancesToCylinder(
          pointCloudInput,
          cylinderData,
          (progress) => {
            this.$emit('corrosion-progress', progress * 0.7) // 0-70% for calculation
          },
          activeClipVolumes.length > 0 ? activeClipVolumes : null
        )

        this.corrosionMode.distances = distancesData.distances
        this.corrosionMode.allPoints = distancesData.allPoints
        this.corrosionMode.clipVolumeActive = activeClipVolumes.length > 0

        debugLog('[Corrosion] Step 1 complete:', {
          pointsProcessed: distancesData.distances.length
        })

        // Step 2: Generate statistics
        this.$emit('corrosion-progress', 75)
        debugLog('[Corrosion] Step 2: Generating statistics...')

        // Use default thresholds for initial statistics calculation
        // Default thresholds aligned with enums.js category boundaries
        const defaultThresholds = {
          normalToMinor: 0.004,      // 4mm - Cat A -> Cat B
          minorToModerate: 0.006,    // 6mm - Cat B -> Cat C
          moderateToCritical: 0.009  // 9mm - Cat C -> Cat D
        }

        // Check if lassos are active and filter distances accordingly
        let statisticsDistances = distancesData.distances
        let lassoFiltered = false
        let lassoFilteredCount = 0
        let lassoTotalCount = distancesData.distances.length

        if (window.AVA_LASSO_DATA && window.AVA_LASSO_DATA.enabled) {
          const { filterDistancesByLasso } = require('../utils/lassoShaderGPU')
          const filterResult = filterDistancesByLasso(
            distancesData.distances,
            distancesData.allPoints
          )
          if (filterResult.filtered && filterResult.filteredDistances.length > 0) {
            statisticsDistances = filterResult.filteredDistances
            lassoFiltered = true
            lassoFilteredCount = filterResult.filteredCount
          }
        }

        const statistics = getCorrosionStatistics(
          statisticsDistances,
          cylinderData.radius,
          defaultThresholds
        )

        // Add lasso filter metadata to statistics
        statistics.lassoFiltered = lassoFiltered
        statistics.lassoFilteredCount = lassoFilteredCount
        statistics.lassoTotalCount = lassoTotalCount

        // Store thresholds for later use
        this.corrosionMode.thresholds = defaultThresholds
        this.corrosionMode.statistics = statistics
        this.$emit('corrosion-stats-updated', statistics, this.corrosionMode.clipVolumeActive)

        debugLog('[Corrosion] Step 2 complete:', {
          healthScore: statistics.healthScore
        })

        // Step 3: Apply shader-based visualization (GPU calculation)
        this.$emit('corrosion-progress', 85)
        debugLog('[Corrosion] Step 3: Applying shader-based visualization...')

        const gradient = getGradientByStyle(gradientStyle)
        const maxDistance = Math.min(statistics.maxDistance, 0.015) // Cap at 15mm (0.015m) for better visualization

        // Store cylinder data for later use (gradient changes, etc.)
        this.corrosionMode.cylinderData = cylinderData

        debugLog(`%c PointClouds`, 'color: #4fc3f7; font-weight: bold; font-size: 14px', this.pointcloud)

        // CRITICAL FIX: Use the ACTUAL material from the rendered point cloud
        // window.AVA_POTREE_VIEWER.scene.pointclouds[0].material is what the GPU renders
        // this.pointcloud.material is a DIFFERENT instance (not rendered)
        const renderPointClouds = (visiblePointClouds.length > 0
          ? visiblePointClouds
          : window.AVA_POTREE_VIEWER.scene.pointclouds
        ) || []

        const renderMaterials = renderPointClouds
          .map((pc) => pc?.material)
          .filter((material) => material)

        if (renderMaterials.length === 0) {
          throw new Error('No render materials available for corrosion shader')
        }

        debugLog('[Corrosion] Using render material UUIDs:', renderMaterials.map((m) => m.uuid))
        debugLog('[Corrosion] Local pointcloud material UUID:', this.pointcloud.material.uuid)

        // Apply shader to material (distances calculated on GPU)
        applyCorrosionShader(
          renderMaterials,
          cylinderData,
          gradient,
          maxDistance
        )

        this.corrosionMode.gradientStyle = gradientStyle
        this.corrosionMode.enabled = true

        debugLog('[Corrosion] Step 3 complete - Shader applied')

        // Final: Force render update
        if (this.viewer.renderer) {
          this.viewer.renderer.render(this.viewer.scene.scene, this.viewer.scene.getActiveCamera())
        }

        this.$emit('corrosion-progress', 100)
        this.$emit('corrosion-enabled', { statistics, cylinderData, clipVolumeActive: this.corrosionMode.clipVolumeActive })

        debugLog('[Corrosion] ✅ Corrosion visualization enabled successfully')

        return statistics

      } catch (error) {
        debugError('[Corrosion] ❌ Error applying corrosion visualization:', error)
        this.$emit('corrosion-error', error)
        throw error
      }
    },

    /**
     * Disable corrosion visualization and restore original colors
     */
    disableCorrosionVisualization() {
      if (!this.pointcloud || !this.corrosionMode.enabled) {
        return
      }

      debugLog('[Corrosion] Disabling corrosion visualization...')

      try {
        var visiblePointClouds = window.AVA_POTREE_VIEWER?.scene?.pointclouds?.filter((pc) => pc?.visible) || []

        // CRITICAL FIX: Use the ACTUAL rendered material
        const renderMaterials = visiblePointClouds.map(pc => pc.material)

        // Disable shader-based visualization
        disableCorrosionShader(renderMaterials)

        // Clear corrosion mode state
        this.corrosionMode.enabled = false
        this.corrosionMode.distances = null
        this.corrosionMode.allPoints = null
        this.corrosionMode.statistics = null
        this.corrosionMode.cylinderData = null

        // Force render update
        if (this.viewer.renderer) {
          this.viewer.renderer.render(this.viewer.scene.scene, this.viewer.scene.getActiveCamera())
        }

        this.$emit('corrosion-disabled')

        debugLog('[Corrosion] ✅ Corrosion visualization disabled')

      } catch (error) {
        debugError('[Corrosion] Error disabling corrosion visualization:', error)
        this.$emit('corrosion-error', error)
      }
    },

    /**
     * Check if corrosion visualization is currently active
     * @returns {boolean}
     */
    isCorrosionVisualizationActive() {
      return this.corrosionMode.enabled
    },

    /**
     * Get current corrosion statistics
     * @returns {Object|null}
     */
    getCorrosionStatistics() {
      return this.corrosionMode.statistics
    },

    /**
     * Change the gradient style for corrosion visualization
     * @param {string} style - Gradient style name
     */
    changeCorrosionGradientStyle(style) {
      if (!this.corrosionMode.enabled || !this.pointcloud) {
        return
      }

      debugLog('[Corrosion] Changing gradient style to:', style)

      const gradient = getGradientByStyle(style)

      // CRITICAL FIX: Use the ACTUAL rendered material
      const renderMaterial = window.AVA_POTREE_VIEWER.scene.pointclouds[0].material

      // Update shader gradient
      updateCorrosionGradient(renderMaterial, gradient)

      this.corrosionMode.gradientStyle = style

      // Force render update
      if (this.viewer.renderer) {
        this.viewer.renderer.render(this.viewer.scene.scene, this.viewer.scene.getActiveCamera())
      }
    },

    /**
     * Export corrosion data to CSV
     * @param {Object} cylinderData - Cylinder parameters
     * @returns {string} CSV data
     */
    exportCorrosionData(cylinderData) {
      if (!this.corrosionMode.distances || !this.corrosionMode.statistics) {
        throw new Error('No corrosion data available to export')
      }

      return exportCorrosionDataToCSV(
        this.corrosionMode.distances,
        this.corrosionMode.statistics,
        cylinderData
      )
    },

    /**
     * Calculate perpendicular distance from a point to the cylinder surface
     * @param {THREE.Vector3} point - Point to measure from
     * @param {Object} cylinderData - Cylinder parameters {position, axis, radius, height}
     * @returns {number} Distance in meters (positive = outside cylinder, negative = inside)
     */
    calculatePointDistanceToCylinder(point, cylinderData) {
      const THREE = window.THREE
      if (!THREE || !point || !cylinderData) return null

      try {
        const cylinderPosition = new THREE.Vector3(
          cylinderData.position.x,
          cylinderData.position.y,
          cylinderData.position.z
        )
        const cylinderAxis = new THREE.Vector3(
          cylinderData.axis.x,
          cylinderData.axis.y,
          cylinderData.axis.z
        ).normalize()

        // Vector from cylinder center to point
        const toPoint = new THREE.Vector3().subVectors(point, cylinderPosition)

        // Project onto cylinder axis to get axial distance
        const axialDistance = toPoint.dot(cylinderAxis)

        // Get perpendicular component (distance from axis)
        const axialComponent = cylinderAxis.clone().multiplyScalar(axialDistance)
        const perpendicular = new THREE.Vector3().subVectors(toPoint, axialComponent)
        const perpendicularDistance = perpendicular.length()

        // Distance from surface (positive = outside, negative = inside)
        const distanceFromSurface = perpendicularDistance - cylinderData.radius

        return distanceFromSurface
      } catch (error) {
        debugError('[PotreeViewer] Error calculating corrosion distance:', error)
        return null
      }
    },

    /**
     * Format corrosion distance for display
     * @param {number} distance - Distance in meters
     * @returns {string} Formatted distance string with unit
     */
    formatCorrosionDistance(distance) {
      if (distance === null || distance === undefined) return '---'

      // Convert to millimeters and use absolute value for display
      const distanceMm = Math.abs(distance * 1000)
      return `${distanceMm.toFixed(2)} mm`
    },

    /**
     * Get CSS class for corrosion severity based on distance
     * @param {number} distance - Distance in meters
     * @returns {string} CSS class name
     */
    getCorrosionSeverityClass(distance) {
      if (distance === null || distance === undefined) return ''

      // Use absolute value for classification
      const absDistance = Math.abs(distance)

      // Classification thresholds (in meters)
      if (absDistance < 0.004) {      // < 4mm - Cat A (Light)
        return 'severity-good'
      } else if (absDistance < 0.006) { // 4-6mm - Cat B (Moderate)
        return 'severity-minor'
      } else if (absDistance < 0.009) { // 6-9mm - Cat C (Heavy)
        return 'severity-moderate'
      } else {                          // > 9mm - Cat D (Severe)
        return 'severity-critical'
      }
    },

    /**
     * Update corrosion gradient with custom thresholds
     * This allows real-time threshold adjustment without re-enabling corrosion mode
     *
     * @param {Object} thresholds - Custom thresholds in meters {normalToMinor, minorToModerate, moderateToCritical, maxDisplay}
     */
    updateCorrosionGradientWithThresholds(thresholds) {
      if (!this.corrosionMode.enabled || !this.pointcloud) {
        debugWarn('[Corrosion] Cannot update thresholds - corrosion mode not enabled')
        return
      }

      debugLog('[Corrosion] Updating gradient with custom thresholds:', thresholds)

      try {
        // Create custom gradient based on thresholds
        const customGradient = createCustomGradient(thresholds)

        // Get the rendered material
        const renderMaterial = window.AVA_POTREE_VIEWER.scene.pointclouds[0].material

        // Update shader with new gradient and thresholds
        const success = updateCorrosionThresholds(
          renderMaterial,
          this.corrosionMode.cylinderData,
          customGradient,
          thresholds.maxDisplay
        )

        if (success) {
          // Store current thresholds for later use
          this.corrosionMode.thresholds = thresholds

          // Force render update
          if (this.viewer.renderer) {
            this.viewer.renderer.render(this.viewer.scene.scene, this.viewer.scene.getActiveCamera())
          }

          // Recalculate statistics with new thresholds
          this.recalculateCorrosionStatistics(thresholds)

          debugLog('[Corrosion] ✅ Gradient and statistics updated successfully with custom thresholds')
          this.$emit('corrosion-gradient-updated', thresholds)
        } else {
          debugError('[Corrosion] Failed to update gradient')
        }

      } catch (error) {
        debugError('[Corrosion] Error updating gradient:', error)
        this.$emit('corrosion-error', error)
      }
    },

    /**
     * Recalculate corrosion statistics with new thresholds
     * This allows dynamic recalculation of percentages, counts, and health score
     * when user adjusts threshold values
     *
     * @param {Object} thresholds - New threshold values in meters
     */
    recalculateCorrosionStatistics(thresholds) {
      if (!this.corrosionMode.distances || !this.corrosionMode.cylinderData) {
        debugWarn('[Corrosion] Cannot recalculate - no distances data available')
        return
      }

      try {
        // Determine which distances to use - apply lasso filtering if active
        let statisticsDistances = this.corrosionMode.distances
        let lassoFiltered = false
        let lassoFilteredCount = this.corrosionMode.distances.length
        let lassoTotalCount = this.corrosionMode.distances.length

        // Check if lasso filtering should be applied
        if (window.AVA_LASSO_DATA && window.AVA_LASSO_DATA.enabled && this.corrosionMode.allPoints) {
          const { filterDistancesByLasso } = require('../utils/lassoShaderGPU')
          const filterResult = filterDistancesByLasso(
            this.corrosionMode.distances,
            this.corrosionMode.allPoints
          )

          if (filterResult.filtered && filterResult.filteredDistances.length > 0) {
            statisticsDistances = filterResult.filteredDistances
            lassoFiltered = true
            lassoFilteredCount = filterResult.filteredCount
            lassoTotalCount = filterResult.totalCount
          }
        }

        // Update lasso filter info
        this.corrosionMode.lassoFiltered = lassoFiltered
        this.corrosionMode.lassoFilteredCount = lassoFilteredCount
        this.corrosionMode.lassoTotalCount = lassoTotalCount

        // Recalculate statistics using (possibly filtered) distances and new thresholds
        const newStatistics = getCorrosionStatistics(
          statisticsDistances,
          this.corrosionMode.cylinderData.radius,
          thresholds
        )

        // Add lasso metadata to statistics
        newStatistics.lassoFiltered = lassoFiltered
        newStatistics.lassoFilteredCount = lassoFilteredCount
        newStatistics.lassoTotalCount = lassoTotalCount

        // Update stored statistics
        this.corrosionMode.statistics = newStatistics

        // Emit updated statistics to parent components
        this.$emit('corrosion-stats-updated', newStatistics, this.corrosionMode.clipVolumeActive)

      } catch (error) {
        debugError('[Corrosion] Error recalculating statistics:', error)
        this.$emit('corrosion-error', error)
      }
    },

    /**
     * Recalculate corrosion statistics for lasso selection
     * Filters distances to only include points inside active lasso(s)
     * and recalculates statistics for the filtered set
     */
    recalculateCorrosionForLassoSelection() {
      if (!this.corrosionMode.distances || !this.corrosionMode.allPoints || !this.corrosionMode.cylinderData) {
        debugWarn('[Corrosion] Cannot recalculate for lasso - no corrosion data available')
        return
      }

      try {
        // Import the filterDistancesByLasso function
        const { filterDistancesByLasso } = require('../utils/lassoShaderGPU')

        // Filter distances by active lasso selection
        const filterResult = filterDistancesByLasso(
          this.corrosionMode.distances,
          this.corrosionMode.allPoints
        )

        // Store filter info for display
        this.corrosionMode.lassoFiltered = filterResult.filtered
        this.corrosionMode.lassoFilteredCount = filterResult.filteredCount
        this.corrosionMode.lassoTotalCount = filterResult.totalCount

        // Get current thresholds from stored values
        const thresholds = this.corrosionMode.thresholds || {
          normalToMinor: 0.004,      // 4mm - Cat A -> Cat B
          minorToModerate: 0.006,    // 6mm - Cat B -> Cat C
          moderateToCritical: 0.009  // 9mm - Cat C -> Cat D
        }

        // Recalculate statistics with filtered distances
        const newStatistics = getCorrosionStatistics(
          filterResult.filteredDistances,
          this.corrosionMode.cylinderData.radius,
          thresholds
        )

        // Add filter metadata to statistics
        newStatistics.lassoFiltered = filterResult.filtered
        newStatistics.lassoFilteredCount = filterResult.filteredCount
        newStatistics.lassoTotalCount = filterResult.totalCount

        // Update stored statistics
        this.corrosionMode.statistics = newStatistics

        // Emit updated statistics to parent components
        this.$emit('corrosion-stats-updated', newStatistics, this.corrosionMode.clipVolumeActive)

      } catch (error) {
        debugError('[Corrosion] Error recalculating for lasso selection:', error)
        this.$emit('corrosion-error', error)
      }
    },

    /**
     * Update classification visibility flags
     * This allows toggling visibility of specific corrosion severity levels
     *
     * @param {Object} visibilitySettings - Visibility settings {normal, minorWear, moderate, critical}
     */
    updateCorrosionVisibility(visibilitySettings) {
      if (!this.corrosionMode.enabled || !this.pointcloud) {
        debugWarn('[Corrosion] Cannot update visibility - corrosion mode not enabled')
        return
      }

      debugLog('[Corrosion] Updating classification visibility:', visibilitySettings)

      try {
        // Import the updateVisibilityFlags function
        const { updateVisibilityFlags } = require('../utils/corrosionShader')

        // Get the rendered material
        const renderMaterial = window.AVA_POTREE_VIEWER.scene.pointclouds[0].material

        // Update visibility flags in the global corrosion data
        const success = updateVisibilityFlags(visibilitySettings, renderMaterial)

        if (success) {
          // Force render update
          if (this.viewer.renderer) {
            this.viewer.renderer.render(this.viewer.scene.scene, this.viewer.scene.getActiveCamera())
          }

          debugLog('[Corrosion] ✅ Visibility flags updated successfully')
          this.$emit('corrosion-visibility-updated', visibilitySettings)
        } else {
          debugError('[Corrosion] Failed to update visibility flags')
        }

      } catch (error) {
        debugError('[Corrosion] Error updating visibility:', error)
        this.$emit('corrosion-error', error)
      }
    },

    /**
     * Capture a screenshot of the current Potree viewer
     * Returns base64 PNG data URL
     * Requires corrosion mode to be active
     */
    captureSnapshot() {
      if (!this.viewer || !this.viewer.renderer) {
        debugWarn('[PotreeViewer] Cannot capture: viewer not ready');
        return null;
      }

      if (!this.corrosionMode.enabled) {
        debugWarn('[PotreeViewer] Cannot capture: corrosion mode not active');
        return null;
      }

      try {
        const canvas = this.viewer.renderer.domElement;
        const dataUrl = canvas.toDataURL('image/png');
        debugLog('[PotreeViewer] ✅ Snapshot captured');
        return dataUrl;
      } catch (error) {
        debugError('[PotreeViewer] ❌ Capture failed:', error);
        return null;
      }
    }
  }
}
</script>

<style scoped>
.potree-container {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  overflow: hidden;
}

#potree_render_area {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

#potree_sidebar_container {
  display: none !important;
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  z-index: 1000;
}

/* Mouse Position Top Sheet - CAD Instrument Panel Style */
.mouse-position-sheet {
  --mp-bg-primary: #2b2b2b;
  --mp-bg-elevated: #323232;
  --mp-accent: #03624c;
  --mp-accent-light: #a8c97a;
  --mp-accent-dark: #5a7a3a;
  --mp-text-primary: #e0e0e0;
  --mp-text-secondary: #a0a0a0;
  --mp-border: rgba(3, 98, 76, 0.4);

  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--mp-bg-primary);
  color: var(--mp-text-primary);
  padding: 10px 16px;
  border-radius: 6px;
  z-index: var(--z-viewer-mouse-info); /* 1500 - Always visible on top */
  display: flex;
  align-items: center;
  gap: 14px;
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 0 0 1px rgba(0, 0, 0, 0.3);
  border: 2px solid var(--mp-border);
  transition: all 0.15s ease;
  pointer-events: none; /* Don't block clicks */
}

.position-label {
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-size: 10px;
  padding: 4px 10px;
  background: rgba(3, 98, 76, 0.15);
  border-radius: 4px;
  border: 1px solid rgba(3, 98, 76, 0.25);
}

.position-coords {
  display: flex;
  gap: 8px;
  align-items: center;
}

.coord-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: var(--mp-bg-elevated);
  border-radius: 4px;
  border-left: 3px solid;
  transition: all 0.15s ease;
}

.coord-item:hover {
  background: #3a3a3a;
}

.coord-item:nth-child(1) {
  border-left-color: #e57373; /* Softer red for X axis */
}

.coord-item:nth-child(2) {
  border-left-color: var(--mp-accent-light); /* Green for Y axis - matches theme */
}

.coord-item:nth-child(3) {
  border-left-color: #64b5f6; /* Softer blue for Z axis */
}

/* Corrosion coordinate styling with severity-based colors */
.coord-item.corrosion-coord {
  border-left-width: 4px;
  background: rgba(3, 98, 76, 0.1);
  transition: all 0.2s ease;
}

.coord-item.corrosion-coord.severity-good {
  border-left-color: var(--mp-accent-light) !important;
  background: rgba(3, 98, 76, 0.12) !important;
}

.coord-item.corrosion-coord.severity-minor {
  border-left-color: #ffd54f !important;
  background: rgba(255, 213, 79, 0.1) !important;
}

.coord-item.corrosion-coord.severity-moderate {
  border-left-color: #ffb74d !important;
  background: rgba(255, 183, 77, 0.1) !important;
}

.coord-item.corrosion-coord.severity-critical {
  border-left-color: #ef5350 !important;
  background: rgba(239, 83, 80, 0.12) !important;
}

.coord-label {
  font-weight: 700;
  font-size: 10px;
  color: var(--mp-text-secondary);
  letter-spacing: 0.5px;
}

.coord-value {
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', monospace;
  font-weight: 500;
  min-width: 72px;
  text-align: right;
  color: var(--mp-text-primary);
  font-variant-numeric: tabular-nums;
}

.coord-value.corrosion-value {
  color: #fff;
  font-weight: 600;
  min-width: 80px;
}
</style>